import type { ProjectileHittable, RemoteDoor, SomethingWithState } from "../interfaces";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import { PhysicalThing } from "../physicalThing";
import { deserializeXY } from "../serialization";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";

export enum BlastDoorState {
	OPEN = "OPEN",
	CLOSED = "CLOSED",
}

export class BlastDoor extends PhysicalThing implements ProjectileHittable, SomethingWithState, RemoteDoor {
	static key = "BlastDoor";
	private state: BlastDoorState = BlastDoorState.CLOSED;
	readonly doorId: string | null;

	constructor(xy: XY, doorId?: string) {
		super(xy, true);
		this.doorId = doorId ?? null;
	}

	static override initOnBoard(xy: XY, board: SimpleBoard, props?: Record<string, unknown>): PhysicalThing[] {
		const door = new BlastDoor(xy, props?.group as string | undefined);
		board.putThing(door);
		return [door];
	}

	getZOrder(): number {
		return ZOrder.SCENARIO;
	}

	onProjectileHit(_projectile: unknown): void {
		this.board!.addNonPhysicalThing(new Sound(SoundId.METAL_BANG, this.xy));
	}

	isHittable(): boolean {
		return this.state === BlastDoorState.CLOSED;
	}

	open(): void {
		if (this.state === BlastDoorState.OPEN) return;
		this.state = BlastDoorState.OPEN;
		this.blocksMovement = false;
		if (this.board) {
			this.board.addNonPhysicalThing(new Sound(SoundId.BLAST_DOOR_OPEN, this.xy));
		}
	}

	close(): void {
		if (this.state === BlastDoorState.CLOSED) return;
		this.state = BlastDoorState.CLOSED;
		this.blocksMovement = true;
		if (this.board) {
			this.board.addNonPhysicalThing(new Sound(SoundId.BLAST_DOOR_OPEN, this.xy));
		}
	}

	getState(): BlastDoorState {
		return this.state;
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), state: this.state, doorId: this.doorId };
	}

	static deserialize(data: any): BlastDoor {
		const d = new BlastDoor(deserializeXY(data.xy), data.doorId ?? undefined);
		d.visualId = data.visualId;
		if (data.state === BlastDoorState.OPEN) {
			d.state = BlastDoorState.OPEN;
			d.blocksMovement = false;
		}
		return d;
	}
}
